import { createStyles, Group, LoadingOverlay, Pagination, Paper, Select, Table, } from '@mantine/core';
import { ReactNode, useEffect, useState } from 'react';
import { useMediaQuery } from '@mantine/hooks';
import {useLazyListenerQuery} from "../store/slice/customApiSlice";

export const Loading = () => {
  return (
    <LoadingOverlay
      loaderProps={{ size: 'md', color: 'blue', variant: 'bars' }}
      overlayOpacity={0.2}
      overlayColor="#c5c5c5"
      visible
    />
  );
};

export type HeadCell<E> = {
  label: string;
  sort?: string;
  render?: (entity: E) => ReactNode;
  afterRender?: Boolean;
};

type Order = 'asc' | 'desc';

type Props<E> = {
  headCells: HeadCell<E>[];
  searchApiUrl: string;
  onRowClick?: (row: E) => void;
  extraFilter?: object;
};

type SearchResponse<T> = {
  content: T[];
  page: number;
  totalElements: number;
  totalPages: number;
};

type SearchResponseLoader<T> = {
  data: SearchResponse<T>;
  loading: boolean;
};

const initResponse = {
  data: { content: [], page: 0, totalElements: 0, totalPages: 0 },
  loading: false,
};

export const AppTable = <Entity extends { id: number }>({
  headCells,
  searchApiUrl,
  onRowClick,
  extraFilter,
}: Props<Entity>) => {
  const [searchRequest, { data: searchData, isLoading }] =
    useLazyListenerQuery();
  const mobileSize = useMediaQuery("(max-width: 768px)")
  const rowsPerPageOptions = ['10', '15'];
  const [response, setResponse] = useState<
    SearchResponseLoader<Entity> & { loading: boolean }
  >(searchData ?? initResponse);
  const [[page, rowsPerPage], setPagination] = useState([0, 10]);
  const { content, totalElements } = response.data ?? initResponse.data;
  const { classes } = useStyles({ rowsCount: content.length, rowsPerPage });
  const [[order, orderBy], setOrder] = useState<[Order, string]>([
    'desc',
    'ID',
  ]);

  const sortDirection = order === 'asc' ? 'ASC' : 'DESC';
  const search = async () => {
    setResponse({ ...response, loading: true });
    return await searchRequest({
      path: searchApiUrl,
      method: 'POST',
      body: {
        filter: {
          ...extraFilter,
        },
        pageRequest: {
          limit: rowsPerPage,
          page: page,
        },
        sorting: {
          sortBy: orderBy,
          sortDirection: sortDirection,
        },
      },
    });
  };

  useEffect(() => {
    search().then((res) => {
      setResponse({ data: res.data, loading: false });
    });
  }, [page, rowsPerPage, extraFilter, order]);

  const getPageRecordInfo = () => {
    const firstRowNum = page * rowsPerPage + 1;
    let lastRowNum = firstRowNum + content.length - 1;

    return `${firstRowNum} - ${lastRowNum} из ${totalElements}`;
  };
  const handleRequestSort = (property: string) =>
    setOrder(([order, orderBy]) => [
      orderBy === property && order === 'asc' ? 'desc' : 'asc',
      property,
    ]);
  const handleRowClick = (row: Entity) => onRowClick?.(row);
  const handleChangePage = (newPage: number) =>
    setPagination([newPage - 1, rowsPerPage]);
  const handleChangeRowsPerPage = (event: string | null) => {
    if (event) {
      setPagination([0, Number.parseInt(event)]);
    }
  };

  const ths = (
    <tr>
      {headCells.map((headCell) => {
        const isActive = orderBy === headCell.sort;
        const hiddenLabel = isActive && (
          <span
            style={{
              display: 'none',
            }}
          >
            {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
          </span>
        );

        if (headCell.sort) {
          return (
            <th style={{ padding: mobileSize ? "6px 3px" : "12px 10px" }}
              key={headCell.label}
              onClick={() => {
                headCell.sort && handleRequestSort(headCell.sort);
              }}
            >
              {headCell.label}
              {hiddenLabel}
            </th>
          );
        }
        return <th key={headCell.label} style={{ padding: mobileSize ? "6px 3px" : "12px 10px" }}>{headCell.label}</th>;
      })}
    </tr>
  );

  const rows = content.map((row, index) => (
    <tr key={index} onClick={() => handleRowClick(row)} style={{
      padding: '0',
      margin: '0',
    }}>
      {headCells.map((cell) => {
        if (cell.label === 'ID') {
          return <td style={{
            margin: '0',
            padding: '0',
          }} key={cell.label}>{row?.id}</td>;
        } else if (cell.label === '№') {
          return <td
            style={{
              margin: '0',
              padding: '0',
              textAlign: 'center',
              alignItems: 'center',
            }}
            key={cell.label}>{index + 1 + page * rowsPerPage}</td>;
        } else {
          return <td
            style={{
              margin: '0',
              padding: '0',
              wordBreak: 'break-word',
              wordWrap: 'break-word',
            }}
            key={cell.label}>{cell.render?.(row)}</td>;
        }
      })}
    </tr>
  ));

  return (
    <Paper radius="md" className={classes.paper}>
      {isLoading ? <Loading /> : null}
      <Table captionSide="bottom" striped highlightOnHover verticalSpacing="sm" fontSize={mobileSize ? "xs" : "sm"}
        style={{ minWidth: '100%' }}>
        <thead>{ths}</thead>
        <tbody>{rows}</tbody>
      </Table>
      <Group position="right" style={{paddingRight: "10px"}}>
        <p style={{fontSize: mobileSize ? "xs" : "sm"}}>Рядов на странице: </p>
        <Select
          style={{ width: '60px', textAlign: 'center' }}
          variant="filled"
          radius="xl"
          size="xs"
          data={rowsPerPageOptions}
          value={rowsPerPage.toString()}
          onChange={(e) => handleChangeRowsPerPage(e)}
        />
        <p>{getPageRecordInfo()}</p>
        <Pagination
          style={{fontSize: mobileSize ? "xs" : "sm"}}
          // page={Number(page )} //TODO: fix
          total={Math.ceil(totalElements / rowsPerPage)}
          onChange={handleChangePage}
          disabled={isLoading}
          size="sm"
          radius="lg"
          color={isLoading ? 'gray' : 'dark'}
        />
      </Group>
    </Paper>
  );
};

const useStyles = createStyles(
  (
    _,
    props: {
      rowsCount: number;
      rowsPerPage: number;
    }
  ) => {
    const { rowsCount, rowsPerPage } = props;
    return {
      paper: {
        paddingBottom: window.innerWidth > 800 ? '0.7rem' : '10px',
        marginBottom: window.innerWidth > 800 ? '0' : '20px',
        minWidth: '100%',
        maxWidth: '1100px',
        minHeight: '30rem',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      },
      visuallyHidden: {
        border: 0,
        shape: 'rect(0 0 0 0)',
      },
      emptyRows: {
        height: 53 * (rowsPerPage - rowsCount),
      },
    };
  }
);
